import React, { useCallback, useEffect, useRef, useState } from 'react';
import Loading from '../../components/shared/Loading/Loading';
import Button from '../../components/shared/Button/Button';
import { FormGroup, FormLabel, FormSelect, ErrorMessage } from '../../components/shared/Form/Form.styles';
import { Panel, IvaTag, MoneyTag } from '../sales/Sales.styles';
import { EmptyBox } from '../sales/SalesList.styles';
import { ItemList, ItemCard, Chip, LinkButton } from '../../components/promoters/promoters.styles';
import PromoterShareLinks from '../../components/promoters/PromoterShareLinks';
import { listPromoterActivities } from '../../services/promoterApi';
import { activityShareText } from '../../utils/share';
import { formatMoney } from '../../utils/money';
import { formatPercent } from '../../utils/commission';

// Pestaña "Mis actividades": las actividades `active` de las empresas que
// aceptaron al promotor, cada una con SU enlace para compartir
// (https://bokerhub.com/share/activity/<id>?ref=CODIGO). El enlace lo arma el
// backend (`shareUrl`, con Open Graph): nunca se arma con la URL de la SPA.
const ActivitiesTab = ({ mode, promoter, acceptedEmpresas, linksLoaded, onGoToEmpresas, onError }) => {
  const [empresaId, setEmpresaId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const errorRef = useRef(onError);
  errorRef.current = onError;

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setData(await listPromoterActivities(mode, empresaId));
    } catch (err) {
      if (errorRef.current && errorRef.current(err)) return;
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [mode, empresaId]);

  useEffect(() => { load(); }, [load]);

  const activities = (data && data.activities) || [];
  const code = (data && data.referralCode) || promoter.referralCode;
  const noEmpresas = linksLoaded && acceptedEmpresas.length === 0;

  return (
    <>
      <Panel>
        <h2>Tu código de promotor</h2>
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', lineHeight: 1.55 }}>
          Tu código es <strong style={{ fontFamily: 'ui-monospace, Menlo, monospace', letterSpacing: 1 }}>{code || '—'}</strong>
          {promoter.commissionPercent ? <> y tu comisión es de <strong>{formatPercent(promoter.commissionPercent)}</strong> sobre el monto de lista de cada venta vigente.</> : '.'}
          {' '}Comparte siempre el enlace completo de cada actividad: trae tu código y la vista previa con foto.
        </p>
        {acceptedEmpresas.length > 1 && (
          <FormGroup style={{ marginTop: 14, marginBottom: 0, maxWidth: 360 }}>
            <FormLabel htmlFor="act-empresa">Empresa</FormLabel>
            <FormSelect id="act-empresa" value={empresaId} onChange={(e) => setEmpresaId(e.target.value)}>
              <option value="">Todas</option>
              {acceptedEmpresas.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
            </FormSelect>
          </FormGroup>
        )}
      </Panel>

      {error && (
        <ErrorMessage role="alert">
          {error} <LinkButton type="button" onClick={load}>Reintentar</LinkButton>
        </ErrorMessage>
      )}

      {loading && !data ? (
        <Loading text="Cargando actividades..." />
      ) : noEmpresas || (activities.length === 0 && !error) ? (
        <EmptyBox>
          <h3>{noEmpresas ? 'Aún no vendes las actividades de ninguna empresa' : 'No hay actividades para compartir'}</h3>
          <p style={{ marginBottom: 12 }}>
            {noEmpresas
              ? 'Pide vender a una empresa y, cuando te acepte, sus actividades activas aparecerán aquí con tu enlace.'
              : 'Esas empresas no tienen actividades activas por ahora.'}
          </p>
          {noEmpresas && <Button variant="secondary" onClick={onGoToEmpresas}>Buscar empresas</Button>}
        </EmptyBox>
      ) : (
        <ItemList aria-busy={loading}>
          {activities.map((activity) => (
            <ItemCard key={activity.id}>
              <div className="head">
                <span className="title">{activity.title}</span>
                {activity.requireCurp && <Chip>Pide CURP de cada asistente</Chip>}
              </div>
              <div className="meta">
                {activity.empresa && activity.empresa.businessName}
                {activity.location ? ` · ${activity.location}` : ''}
                {activity.duration ? ` · ${activity.duration}` : ''}
              </div>
              <div className="money">
                <span>Precio por persona: <MoneyTag>{formatMoney(activity.price)}</MoneyTag> <IvaTag>IVA incluido</IvaTag></span>
              </div>
              <PromoterShareLinks url={activity.shareUrl} text={activityShareText(activity)} />
            </ItemCard>
          ))}
        </ItemList>
      )}
    </>
  );
};

export default ActivitiesTab;
